// Pure logic for the debt payment-reconcile form. The form edits per-charge and
// legacy amounts as strings; this turns them into the Allocation[] the API and
// validateAllocations consume. Kept free of Svelte/DOM for direct unit testing.

import { round2 } from './format';
import type { Allocation, PaymentSuggestion } from './db';

/** Stable key for a charge's per-row amount in the editor's `chargeAmt` map. */
export function chargeFieldKey(pk: string, sk: string): string {
	return `${pk}|${sk}`;
}

/**
 * Assemble allocations from the editor state:
 * - one `purchase` per charge with a positive typed amount,
 * - a `legacy` allocation when the legacy field is positive,
 * - an `overpayment` for whatever of the payment magnitude is left over.
 *
 * Amounts at or below half a cent are treated as zero (rounding noise).
 */
export function buildAllocations(
	suggestion: PaymentSuggestion,
	chargeAmt: Record<string, string>,
	legacyAmt: string
): Allocation[] {
	const allocs: Allocation[] = [];
	let purchaseSum = 0;
	for (const c of suggestion.unpaidPool) {
		const amt = Number(chargeAmt[chargeFieldKey(c.pk, c.sk)] ?? '0');
		if (amt > 0.005) {
			allocs.push({ kind: 'purchase', targetPk: c.pk, targetSk: c.sk, amount: round2(amt) });
			purchaseSum = round2(purchaseSum + amt);
		}
	}
	const legacy = Number(legacyAmt ?? '0');
	if (legacy > 0.005) allocs.push({ kind: 'legacy', amount: round2(legacy) });
	const over = round2(suggestion.magnitude - purchaseSum - (legacy > 0 ? legacy : 0));
	if (over > 0.005) allocs.push({ kind: 'overpayment', amount: over });
	return allocs;
}
